import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isStandaloneMode } from "@/hooks/use-is-standalone";

/**
 * Wrapper that only renders (and therefore only registers the SW via useRegisterSW)
 * when the app is running in standalone/PWA tablet mode.
 *
 * For regular web visitors (Instagram, Chrome, Safari, WhatsApp, etc.):
 *   - NO Service Worker is registered → no stale cache, always fresh content.
 *   - Any previously-registered SW is actively UNREGISTERED + caches cleaned.
 */
export const AppUpdatePrompt = () => {
  // If NOT in standalone mode → don't register SW, actively clean up any stale one
  if (!isStandaloneMode()) {
    return <SwCleanup />;
  }

  // In standalone mode → register SW normally and show update prompt
  return <StandaloneUpdatePrompt />;
};

/**
 * For non-standalone visitors: unregister any lingering Service Workers and clear
 * workbox runtime caches so they always get fresh content.
 * This does NOT touch localStorage or IndexedDB (preserves user preferences, language, etc.)
 */
function SwCleanup() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((reg) => {
        reg.unregister().then(() => {
          console.log("[SW Cleanup] Unregistered stale SW for web visitor");
        });
      });
    });

    // Clean only workbox runtime caches (NOT localStorage or IndexedDB)
    if (window.caches) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          // Only delete workbox-managed caches (runtime, precache, API caches)
          if (
            name.startsWith("workbox-") ||
            name.startsWith("api-") ||
            name.startsWith("backend-storage-images") ||
            name.startsWith("images") ||
            name.startsWith("google-fonts-")
          ) {
            caches.delete(name);
          }
        });
      });
    }
  }, []);

  return null;
}

/**
 * The actual update prompt UI — only rendered in standalone PWA mode (tablets).
 * This is where useRegisterSW is called, ensuring SW registration only happens for tablets.
 */
function StandaloneUpdatePrompt() {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const isRtl = locale === "ar" || locale === "ku";

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      if (r) {
        // Check immediately on mount
        r.update().catch(() => {});
        // Check immediately when device reconnects to Wi-Fi
        window.addEventListener("online", () => {
          r.update().catch(() => {});
        });
        // Poll every 30s in background
        setInterval(() => r.update().catch(() => {}), 30 * 1000);
      }
    },
  });

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (needRefresh) setDismissed(false);
  }, [needRefresh]);

  const handleUpdate = () => {
    updateServiceWorker(true);
  };

  const handleDismiss = () => {
    setNeedRefresh(false);
    setDismissed(true);
  };

  const label = {
    message:
      locale === "ar"
        ? "يوجد تحديث جديد للتطبيق"
        : locale === "ku"
          ? "نوێکردنەوەی نوێ بۆ بەرنامەکە هەیە"
          : "A new version is available",
    update:
      locale === "ar"
        ? "تحديث الآن"
        : locale === "ku"
          ? "نوێ بکەرەوە"
          : "Update Now",
    dismiss:
      locale === "ar" ? "لاحقاً" : locale === "ku" ? "دواتر" : "Later",
  };

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return null;
  }

  if (window.location.pathname === "/full-menu") {
    return null;
  }

  return (
    <AnimatePresence>
      {needRefresh && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-5 inset-x-4 z-[9999] flex justify-center pointer-events-none"
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className="pointer-events-auto w-full max-w-sm bg-primary text-bodyBg rounded-2xl shadow-2xl shadow-primary/30 flex items-center gap-3 px-4 py-3.5">
            {/* Icon */}
            <div className="shrink-0 bg-white/15 rounded-full p-2">
              <RefreshCw className="size-5 animate-spin [animation-duration:2s]" />
            </div>

            {/* Text */}
            <p className="flex-1 text-sm font-semibold leading-snug">
              {label.message}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleUpdate}
                className="bg-white text-primary text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white/90 transition-colors"
              >
                {label.update}
              </button>
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white transition-colors p-1"
                aria-label="dismiss"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
